import React from 'react';
import {
    Container,
    Typography,
    Box,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemText,
    Link
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import SectionTitle from './shared/SectionTitle';

const HeroSection = styled(Box)(({ theme }) => ({
    background: 'linear-gradient(135deg, #1a2e4a 0%, #2a4a6e 100%)',
    color: '#ffffff',
    padding: theme.spacing(8, 2),
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0L60 30L30 60L0 30z' fill='%23ffffff' fill-opacity='0.02'/%3E%3C/svg%3E")`,
        backgroundSize: '60px 60px',
    },
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    margin: theme.spacing(2, 0),
    backgroundColor: '#fafafa'
}));

const SubSectionTitle = styled(Typography)(({ theme }) => ({
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    fontWeight: 'bold',
    color: theme.palette.primary.main
}));

const PrivacyPolicy: React.FC = () => {
    return (
        <>
            {/* Navy Hero Section */}
            <HeroSection>
                <Box sx={{ position: 'relative', maxWidth: '800px', margin: '0 auto' }}>
                    <Typography 
                        variant="h2" 
                        sx={{ 
                            fontWeight: 500,
                            mb: 2,
                            fontSize: { xs: '2rem', md: '2.75rem' },
                            color: '#ffffff',
                        }}
                    >
                        Privacy Policy
                    </Typography>
                    <Typography 
                        sx={{ 
                            fontSize: '1.15rem',
                            fontWeight: 300,
                            opacity: 0.9,
                            maxWidth: '600px',
                            margin: '0 auto',
                            color: '#ffffff',
                        }}
                    >
                        Last Updated: {new Date().toLocaleDateString()}
                    </Typography>
                </Box>
            </HeroSection>

            <Container maxWidth="md" sx={{ py: 4 }}>

            <StyledPaper elevation={1}>
                <Typography variant="h5" gutterBottom>
                    Our Commitment to Your Privacy
                </Typography>
                <Typography variant="body1" paragraph>
                    At Point Sails, we respect your privacy and are committed to protecting your personal information. 
                    This policy explains what information we collect, how we use it, and your rights regarding your data.
                </Typography>
                
                <Typography variant="body1" paragraph>
                    <strong>Our Core Principles:</strong>
                </Typography>
                <List sx={{ listStyleType: 'disc', pl: 2 }}>
                    <ListItem sx={{ display: 'list-item' }}>
                        <ListItemText primary="We will never sell your personal information to third parties" />
                    </ListItem>
                    <ListItem sx={{ display: 'list-item' }}>
                        <ListItemText primary="We will not share your information without your written permission" />
                    </ListItem>
                    <ListItem sx={{ display: 'list-item' }}>
                        <ListItemText primary="We collect only the minimum information necessary to serve you better" />
                    </ListItem>
                </List>
            </StyledPaper>

            <SectionTitle variant="h5">
                Information We Collect
            </SectionTitle>
            
            <Typography variant="h6" gutterBottom>
                Personal Information You Provide
            </Typography>
            <Typography variant="body1" paragraph>
                We collect information only when you voluntarily provide it to us:
            </Typography>
            <List sx={{ listStyleType: 'disc', pl: 2 }}>
                <ListItem>
                    <ListItemText 
                        primary="Email Address" 
                        secondary="Required for password resets, cart tracking, order confirmations, and important communications about your orders"
                    />
                </ListItem>
                <ListItem>
                    <ListItemText 
                        primary="Name (First and Last)" 
                        secondary="Required only if you create an account - used to personalize your experience and for order processing"
                    />
                </ListItem>
                <ListItem>
                    <ListItemText 
                        primary="Username" 
                        secondary="Required only if you create an account - for account identification if you choose to create an account"
                    />
                </ListItem>
                <ListItem>
                    <ListItemText 
                        primary="Sail Specifications and Measurements" 
                        secondary="Only when you request quotes or submit measurement forms"
                    />
                </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Information We Automatically Collect
            </Typography>
            <Typography variant="body1" paragraph>
                To provide a better user experience, we automatically collect minimal technical information:
            </Typography>
            <List>
                <ListItem>
                    <ListItemText 
                        primary="Cart Contents" 
                        secondary="We track items in your cart to preserve your quotes between visits"
                    />
                </ListItem>
                <ListItem>
                    <ListItemText 
                        primary="Session Information" 
                        secondary="Basic session data to keep you logged in and maintain your preferences"
                    />
                </ListItem>
            </List>

            <SectionTitle variant="h5">
                How We Use Cookies
            </SectionTitle>
            <Typography variant="body1" paragraph>
                We use cookies to enhance your browsing experience. Here's what we store and why:
            </Typography>
            <List>
                <ListItem>
                    <ListItemText 
                        primary="Authentication Cookies" 
                        secondary="Keep you logged in so you don't have to sign in repeatedly"
                    />
                </ListItem>
                <ListItem>
                    <ListItemText 
                        primary="Cart Persistence" 
                        secondary="Remember your quotes and cart items between visits"
                    />
                </ListItem>
                <ListItem>
                    <ListItemText 
                        primary="Session Management" 
                        secondary="Track your session activity to provide a smooth experience"
                    />
                </ListItem>
            </List>
            <Typography variant="body2" paragraph sx={{ fontStyle: 'italic' }}>
                Note: We do not use cookies for advertising or tracking your behavior across other websites.
            </Typography>

            <SectionTitle variant="h5">
                Anonymous User Experience
            </SectionTitle>
            <Typography variant="body1" paragraph>
                You can use our site without creating an account. When you do this:
            </Typography>
            <List sx={{ listStyleType: 'disc', pl: 2 }}>
                <ListItem sx={{ display: 'list-item' }}>
                    <ListItemText 
                        primary="We create a temporary anonymous identifier to persist your sail configuration selections"
                    />
                </ListItem>
                <ListItem sx={{ display: 'list-item' }}>
                    <ListItemText 
                        primary="No personal information is required - you remain completely anonymous"
                    />
                </ListItem>
                <ListItem sx={{ display: 'list-item' }}>
                    <ListItemText 
                        primary="You can convert to a full account later if you choose without having to re-enter sail configurations"
                    />
                </ListItem>
            </List>

            <SectionTitle variant="h5">
                How We Use Your Information
            </SectionTitle>
            <Typography variant="body1" paragraph>
                We use your information only for legitimate business purposes:
            </Typography>
            <List sx={{ listStyleType: 'disc', pl: 2 }}>
                <ListItem sx={{ display: 'list-item' }}>
                    <ListItemText primary="Processing and fulfilling your sail orders" />
                </ListItem>
                <ListItem sx={{ display: 'list-item' }}>
                    <ListItemText primary="Providing customer support and answering questions" />
                </ListItem>
                <ListItem sx={{ display: 'list-item' }}>
                    <ListItemText primary="Sending order confirmations and updates" />
                </ListItem>
                <ListItem sx={{ display: 'list-item' }}>
                    <ListItemText primary="Password resets when you request them" />
                </ListItem>
                <ListItem sx={{ display: 'list-item' }}>
                    <ListItemText primary="Improving our services based on your feedback" />
                </ListItem>
            </List>

            <SectionTitle variant="h5">
                Information Sharing
            </SectionTitle>
            <Typography variant="body1" paragraph>
                <strong>We do not sell, rent, or trade your personal information.</strong> We may share information only in these limited circumstances:
            </Typography>
            <List>
                <ListItem>
                    <ListItemText 
                        primary="Service Providers" 
                        secondary="With trusted partners who help us fulfill orders like shipping companies (UPS, FedEx, USPS...)"
                    />
                </ListItem>
                <ListItem>
                    <ListItemText 
                        primary="Legal Requirements" 
                        secondary="When required by law or to protect our legal rights"
                    />
                </ListItem>
                <ListItem>
                    <ListItemText 
                        primary="With Your Consent" 
                        secondary="Only when you explicitly authorize us to share specific information"
                    />
                </ListItem>
            </List>

            <SectionTitle variant="h5">
                Data Security
            </SectionTitle>
            <Typography variant="body1" paragraph>
                We implement appropriate security measures to protect your information:
            </Typography>
            <List sx={{ listStyleType: 'disc', pl: 2 }}>
                <ListItem sx={{ display: 'list-item' }}>
                    <ListItemText primary="Secure HTTPS encryption for all data transmission" />
                </ListItem>
                <ListItem sx={{ display: 'list-item' }}>
                    <ListItemText primary="Secure server infrastructure with regular backups" />
                </ListItem>
                <ListItem sx={{ display: 'list-item' }}>
                    <ListItemText primary="Limited access to personal information on a need-to-know basis" />
                </ListItem>
                <ListItem sx={{ display: 'list-item' }}>
                    <ListItemText primary="Regular security updates and monitoring" />
                </ListItem>
            </List>

            <SectionTitle variant="h5">
                Your Rights and Choices
            </SectionTitle>
            <Typography variant="body1" paragraph>
                You have control over your personal information:
            </Typography>
            <List>
                <ListItem>
                    <ListItemText 
                        primary="Access and Update" 
                        secondary="View and update your account information at any time"
                    />
                </ListItem>
                <ListItem>
                    <ListItemText 
                        primary="Delete Account" 
                        secondary="Request deletion of your account and associated data"
                    />
                </ListItem>
                <ListItem>
                    <ListItemText 
                        primary="Data Portability" 
                        secondary="Request a copy of your data in a portable format"
                    />
                </ListItem>
                <ListItem>
                    <ListItemText 
                        primary="Opt-out" 
                        secondary="We may on rare occaisions send informational emails relevant to your prior order or for other legitimate business information. 
                                    You may unsubscribe from these non-essential communications at any time."
                    />
                </ListItem>
            </List>

            <SectionTitle variant="h5">
                Children's Privacy
            </SectionTitle>
            <Typography variant="body1" paragraph>
                Our services are intended for adults. We do not knowingly collect personal information from children under 13. 
                If we become aware that a child has provided us with personal information, we will take steps to delete such information.
            </Typography>

            <SectionTitle variant="h5">
                Changes to This Policy
            </SectionTitle>
            <Typography variant="body1" paragraph>
                We may update this privacy policy from time to time. We will notify you of any material changes by 
                posting the updated policy on this page with a new "Last Updated" date.
            </Typography>

            <SectionTitle variant="h5">
                Contact Us
            </SectionTitle>
            <Typography variant="body1" paragraph>
                If you have any questions about this privacy policy or how we handle your information, please contact us:
            </Typography>
            <List>
                <ListItem>
                    <ListItemText 
                        primary="Email" 
                        secondary={
                            <Link href="mailto:info@pointsails.com" underline="hover">
                                daron@pointsails.com
                            </Link>
                        }
                    />
                </ListItem>
                <ListItem>
                    <ListItemText 
                        primary="Contact Form" 
                        secondary={
                            <Link component={RouterLink} to="/contact" underline="hover">
                                Use our contact form
                            </Link>
                        }
                    />
                </ListItem>
            </List>

            <Divider sx={{ my: 3 }} />
            
            <Typography variant="body2" align="center" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                This privacy policy reflects our commitment to transparency and your privacy rights. 
                We believe in earning your trust through clear communication about our data practices.
            </Typography>
            </Container>
        </>
    );
};

export default PrivacyPolicy;